/**
 * Pure helpers to calculate order supplements.
 *
 * Kept free of framework and data-layer dependencies so they can be unit
 * tested with fake data and reused across the app.
 */

export interface HistoricalSupplement {
  supplement?: number | null
  // eslint-disable-next-line camelcase
  email_sent_at?: string | null
  parent?: number | null
}

/**
 * A supplement is considered "consolidated" when it has already been sent by
 * email or when it has been attached to another supplement (unified/refund).
 *
 * Those amounts must NOT be added again when recalculating a new supplement;
 * only open supplements (not sent and without parent) should still accumulate.
 */
export const isSupplementConsolidated = (sup: HistoricalSupplement): boolean =>
  (sup.email_sent_at ?? null) !== null || (sup.parent ?? null) !== null

/**
 * Sums the amount of every consolidated supplement in the list.
 */
export const sumConsolidatedSupplements = (
  sups: HistoricalSupplement[] | null | undefined
): number =>
  Number(
    (sups || [])
      .filter(isSupplementConsolidated)
      .map((s) => Number(s.supplement || 0))
      .reduce((a, b) => a + b, 0)
      .toFixed(2) || 0
  )

/**
 * Calculates the value of a new price-change supplement.
 *
 * The new supplement is computed as an absolute delta from the original order
 * (`targetPriceDelta`), minus every price change already consolidated
 * (sent by email or added to refunds). This avoids re-adding amounts that
 * were already refunded to the client.
 *
 * @param targetPriceDelta   total price change vs the original order
 * @param historicalSups     existing priceChange supplements
 * @param clonedItemReverted true when a cloned item was reverted to its original values
 */
export const calcPriceChangeSupplementValue = (
  targetPriceDelta: number,
  historicalSups: HistoricalSupplement[] | null | undefined,
  clonedItemReverted: boolean = false
): number => {
  const sentSups = sumConsolidatedSupplements(historicalSups)
  const baseValue = clonedItemReverted ? 0 : targetPriceDelta
  return Number((baseValue - sentSups).toFixed(2))
}
